import { cloneElement, isValidElement } from "react";
import { Controller, type FieldValues, useFormContext } from "react-hook-form";
import ErrorMessage from "../error-message";
import FormLabel from "../form-label";

type FormControlProps = {
  name: string;
  label?: string;
  isOptional?: boolean;
  isRequired?: boolean;
  children: React.ReactElement;
  hasIcon?: boolean;
};

type ChildProps = {
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
};

const FormControl: React.FC<FormControlProps> = ({
  name,
  label,
  isOptional,
  isRequired,
  children,
  hasIcon = false,
}) => {
  const { control } = useFormContext<FieldValues>();

  if (!isValidElement(children)) return null;

  const child = children as React.ReactElement<Record<string, unknown>>;
  const childProps = child.props as ChildProps;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className="w-full flex flex flex-col gap-1">
          <div
            className="relative group flex flex-col gap-1"
            data-empty={!field?.value || field?.value === ""}
            data-has-icon={hasIcon}
          >
            {cloneElement(child, {
              ...(field as Record<string, unknown>),
              onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
                field.onChange(e);
                childProps.onChange?.(e);
              },
              onBlur: (e: React.FocusEvent<HTMLSelectElement>) => {
                field.onBlur();
                childProps.onBlur?.(e);
              },
              state: fieldState.error?.message ? "error" : "default",
            })}
            {label && (
              <FormLabel
                label={label}
                isRequired={isRequired}
                isOptional={isOptional}
              />
            )}
          </div>
          {fieldState?.error?.message && (
            <ErrorMessage message={fieldState?.error.message} />
          )}
        </div>
      )}
    />
  );
};

export default FormControl;
